import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { BackListButton, Loading } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import ReturnShoppingBagIcon from '@/metrics/components/ReturnShoppingBagIcon.svg?react'
import Variant from '@/metrics/components/Variant/Variant'
import VariantMetrics from '@/metrics/components/VariantMetrics/VariantMetrics'
import { getTitleByOrdering } from '@/metrics/helpers/helpers'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import routes from '@/Router/routes.constants'

import useVariantMetrics from './hooks/useVariantMetrics'
import * as Styles from './styles'

const VariantMetricsDetailPage = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const { productPosition, variantPosition } = useParams()
  const { info } = useContext(AppContext)
  const { regions } = useRegions()

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated
  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  } = useFilters()

  const { handleFiltersChange, variant, loadingVariant, parentProduct } = useVariantMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    setCommonFilters,
    commonFilters,
    productPosition,
    variantPosition,
    productsFilters,
  })

  const productPath = isUnifiedDashboard
    ? routes.merchantDashboardProductMetricsDetailPage
    : routes.productMetricsDetailPage

  const getBreadcrumbs = () => {
    const parentProductName = parentProduct?.name
    const variantName = variant?.name || variant?.title

    const breadCrumbs = [
      {
        show: () => parentProductName,
        title: productPosition ? `#${Number(productPosition) + 1} ${t('Producto')}` : t('Producto'),
        subtitle: parentProductName,
        url: `${productPath.replace(':position', productPosition)}?${getUrlSearchParams()}`,
      },
      {
        show: () => productPosition && variantName,
        title: `#${Number(variantPosition) + 1} ${t('Variante')}`,
        subtitle: variantName,
      },
    ]

    return breadCrumbs.filter(breadcrumb => breadcrumb.show())
  }

  const getDashboardUrl = () => {
    return `${isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage}?${getUrlSearchParams()}`
  }

  const getCardTitle = () => {
    if (productPosition) {
      return getTitleByOrdering('Productos', productsFilters?.ordering, t)
    }
    return getTitleByOrdering('Variantes', productsFilters?.ordering, t)
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getDashboardUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={handleFiltersChange} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={ReturnShoppingBagIcon}
        title={getCardTitle()}
        loading={loadingVariant}
        item={
          variant?.variant_id && (
            <Variant variant={{ ...variant, index: Number(variantPosition) + 1 }} showAmounts={false} />
          )
        }
        percentage={variant?.percentage}
        count={variant?.count}
        breadcrumbs={getBreadcrumbs()}
        sales={variant?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
      />
      {variant && (
        <Loading show={loadingVariant}>
          <VariantMetrics
            variantId={variant?.variant_id}
            returnPortalId={isUnifiedDashboard ? null : info?.selectedReturnPortal?.id}
            commonFilters={commonFilters}
            returnSalesRatioFilters={returnSalesRatioFilters}
            setReturnSalesRatioFilters={setReturnSalesRatioFilters}
            returnsAndExchangesRatioFilters={returnsAndExchangesRatioFilters}
            setReturnsAndExchangesRatioFilters={setReturnsAndExchangesRatioFilters}
            salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
          />
        </Loading>
      )}
    </Styles.Container>
  )
}

export default VariantMetricsDetailPage
